import { useIntl } from "react-intl";
import { SKILL_CATEGORIES, SKILL_LEVELS, fromStoragePercent, toStoragePercent } from "../../lib/skill-levels";
import { skillLevelSwatch } from "../../lib/skill-level-style";
import { useThemePrefs } from "../../stores/theme-prefs";
import { SkillLevelInfoIcon } from "./SkillLevelInfoIcon";

// 7 chips grouped into 3 categories. The selected chip is marked with x,
// the matching category gets an underline + level name and an info tooltip.
// value is the genesis percent (0 = unset); onChange emits a percent back so
// the save flow stays a single round-trip. Clicking the active chip toggles
// it off (emits 0), so a level stays clearable rather than mandatory.

interface SkillLevelScaleProps {
  value: number | null | undefined;
  onChange: (percent: number) => void;
  idPrefix?: string;
}

export function SkillLevelScale({ value, onChange, idPrefix }: SkillLevelScaleProps) {
  const intl = useIntl();
  const current = fromStoragePercent(value);
  const selected = SKILL_LEVELS.find((l) => l.value === current) ?? null;
  const accentColor = useThemePrefs((s) => s.accentColor);
  const selectedName = selected
    ? intl.formatMessage({ id: selected.nameId, defaultMessage: selected.name })
    : "";
  const selectedTitle = selected
    ? intl.formatMessage({ id: selected.titleId, defaultMessage: selected.name })
    : "";

  return (
    <div>
      <div className="flex gap-3">
        {SKILL_CATEGORIES.map((cat) => {
          const groupSelected = cat.levels.some((l) => l.value === current);
          return (
            <div key={cat.nameId}>
              <div className="flex gap-1.5">
                {cat.levels.map((l) => {
                  const isSel = l.value === current;
                  const name = intl.formatMessage({ id: l.nameId, defaultMessage: l.name });
                  const swatch = skillLevelSwatch(l.value, accentColor);
                  return (
                    <button
                      key={l.value}
                      id={idPrefix && `${idPrefix}-${l.value}`}
                      type="button"
                      aria-label={name}
                      aria-pressed={isSel}
                      onClick={() => onChange(isSel ? 0 : toStoragePercent(l.value))}
                      className={`flex h-8 w-8 items-center justify-center rounded-md transition ${
                        isSel ? "ring-2 ring-ink" : "ring-1 ring-ink/10 hover:ring-ink-ui"
                      }`}
                      style={{ background: swatch.background }}
                    >
                      {isSel ? <span className="text-smycles-meta" style={{ color: swatch.textColor }}>x</span> : null}
                    </button>
                  );
                })}
              </div>
              <div className={`mt-1.5 h-px ${groupSelected ? "bg-ink-ui" : "bg-transparent"}`} />
              {groupSelected && selected ? (
                <div className="mt-1 flex items-center justify-center gap-1 text-center text-smycles-label font-medium leading-tight text-ink">
                  <span>{selectedName}</span>
                  <SkillLevelInfoIcon level={selected} />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="mt-2 min-h-[2.5em] text-smycles-label leading-snug text-ink">
        {selected ? (
          <>
            <span className="font-medium text-ink">{selectedTitle}</span>
          </>
        ) : (
          <span className="text-ink-muted">{intl.formatMessage({ id: "design.chooseLevel", defaultMessage: "Choose level." })}</span>
        )}
      </div>
    </div>
  );
}
